import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getTestCycleKeyFromLocation } from '../../../../lib/executionUtils';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import {
    associateIssuesBulk,
    fetchAssociatedIssues
} from '../../../../modules/associations';
import { useProject } from '../../../../context/ProjectContext';
import QUERY_KEYS from '../../../../query/queryKeys';
import CreateJiraIssueForm from './CreateIssueForm';
import SearchIssuesModalComponent from './SearchIssuesModal';

const IssueLinkManager = ({
    entityId,
    entityType,
    data,
    entityKey,
    open,
    onClose,
    failedStep
}) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [issueKey, setIssueKey] = useState([]);
    const [step, setStep] = useState(1);
    const [submittingAdd, setSubmittingAdd] = useState(false);
    const { addNotification } = useNotification();
    const { projectId } = useParams();
    const location = useLocation();
    const testCycleKey = getTestCycleKeyFromLocation(location);
    const queryClient = useQueryClient();
    const forceRefreshRef = useRef(false);
    const [prefillData, setPrefillData] = useState(null);
    const [selectedProjectFromSearch, setSelectedProjectFromSearch] = useState(null);
    const handleShowCreateModalWithPrefill = (data) => {
        setPrefillData(data);
        setShowCreateModal(true);
        onClose();
    };  
    // When the modal closes → notify parent
    const handleCloseSearch = () => {
        onClose();                // prevents reopening or stacking
    };

    const { currentProject } = useProject();

    const linkedJiraProjects = currentProject?.linked_jira_projects || [];

    const { data: linkedIssues = [] } = useQuery({
        queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
        queryFn: async () => {
            const currentForceRefresh = forceRefreshRef.current;
            forceRefreshRef.current = false;
            return fetchAssociatedIssues(entityId, projectId, currentForceRefresh);
        },
        enabled: !!entityId && !!currentProject && linkedJiraProjects.length > 0,
        select: (res) =>
            res.issues?.map((issue) => ({
                key: issue.key,
                id: issue.jira_issue_id,
                summary: issue.summary,
                status: issue.status,
                issue_type: issue.issue_type,
                priority: issue.priority,
                jiraProjectName: issue.jira_project_name,
            })) || [],
    });

    const linkedIssueIds = linkedIssues.map((i) => i.id);

    const handleAddIssues = async (issues) => {
        const otherData =
            entityType === 'Execution'
                ? { testCaseKey: data?.key, testCycleKey: testCycleKey }
                : null;

        const entityUrl = generateNotificationUrl(
            entityType,
            projectId,
            entityKey,
            'traceability',
            otherData
        );

        const associations = {
            issues: issues.map((issue) => ({
                id: issue.id,
                key: issue.key,
                priority: issue.priority,
                projectId: issue.projectId,

            })),
            entityId,
            entityType,
            gainssProjectId: projectId,
            entityUrl,
            entityKey,
        };

        setSubmittingAdd(true);

        try {
            await associateIssuesBulk(associations, projectId);
            addNotification('success', 'Issue linked successfully.');
            handleRefresh();

            // After linking in Execution mode, go to step 2 (confirmation)
            if (entityType === 'Execution') {
                setIssueKey(issues.map((issue) => issue.key));
                setStep(2);
            } else {
                onClose();
            }
        } catch(error) {
            addNotification('danger', error.message ||'Failed to link issues to the test case.');
        } finally {
            setSubmittingAdd(false);
        }
    };

    const handleRefresh = () => {
        forceRefreshRef.current = true;
        queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
            exact: true,
        });
    };

    const handleShowCreateModal = () => {
        const stepToPrefill = failedStep || step;
        if (!stepToPrefill) return;

        const summaryPrefill = `${data.summary} ${stepToPrefill.actual_result || ''}`;
        const descriptionPrefill = `
Step Summary: ${stepToPrefill.teststeps?.step || ''}

Test Data: ${stepToPrefill.teststeps?.test_data || ''}

Expected Result: ${stepToPrefill.teststeps?.expected_result || ''}

Actual Result: ${stepToPrefill.actual_result || ''}`;

const stepAttachments = stepToPrefill.teststeps?.attachments || [];

        handleShowCreateModalWithPrefill({
            summary: summaryPrefill,
            description: descriptionPrefill,
            attachments: stepAttachments,
        });
    };
 

    return (
        <>
            <SearchIssuesModalComponent
                show={open}
                setShowSearchModal={handleCloseSearch}
                handleAddIssues={handleAddIssues}
                linkedIssueIds={linkedIssueIds}
                currentProject={currentProject}
                step={step}
                issueKey={issueKey}
                testCaseData={data}
                setStep={setStep}
                entityKey={entityKey}
                submitting={submittingAdd}
                setSubmitting={setSubmittingAdd}
                linkedJiraProjects={linkedJiraProjects}
                handleShowCreateModal={handleShowCreateModal}
                setSelectedProjectFromSearch={setSelectedProjectFromSearch}
            />

            <CreateJiraIssueForm
                show={showCreateModal}
                handleClose={() => setShowCreateModal(false)}
                fetchLinkedIssues={handleRefresh}
                entityId={entityId}
                entityType={entityType}
                currentProject={currentProject}
                testCaseData={data}
                entityKey={entityKey}
                linkedJiraProjects={linkedJiraProjects}
                prefillData={prefillData}
                setPrefillData={setPrefillData}
                selectedProjectFromSearch={selectedProjectFromSearch}
            />
        </>
    );
};

export default IssueLinkManager;
